import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { API_URL } from "../apiConfig";
import {
  FaClock,
  FaChevronLeft,
  FaCalendarAlt,
  FaClipboardCheck,
  FaMapMarkerAlt,
  FaGlobe,
  FaHourglassHalf,
  FaArrowRight,
  FaLock,
  FaExclamationTriangle,
  FaSync,
} from "react-icons/fa";

export default function EnrollPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Basic context passed from CourseDetails (course info + selected level)
  const { course, selectedLevel, price, duration } = location.state || {};

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const formatDateString = (dateStr) => {
    if (!dateStr) return "TBC";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-SG", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (schedule) => {
    if (!schedule) return "9:00 AM – 5:00 PM";
    const start = schedule.am_start_time || schedule.pm_start_time || schedule.night_start_time;
    const end = schedule.night_end_time || schedule.pm_end_time || schedule.am_end_time;
    if (!start) return "9:00 AM – 5:00 PM";
    const convert = (t) => {
      if (!t) return "";
      const [hStr, mStr] = t.split(":");
      const h = parseInt(hStr);
      if (isNaN(h)) return t;
      const ampm = h >= 12 ? "PM" : "AM";
      const displayH = h % 12 === 0 ? 12 : h % 12;
      return `${displayH}:${mStr || "00"} ${ampm}`;
    };
    return `${convert(start)} – ${convert(end || "17:00")}`;
  };

  // ─── Filter schedules using the EXACT API fields ──────────────────────────
  // course_level field = "Level 1" or "Level 2" (set directly in TMS)
  // course_title field = e.g. "COC Metal Scaffolding Level 1"
  // We match level via course_level field first, fall back to title if missing.
  // We match course via course_title containing the key subject word(s).

  const SUBJECT_KEYWORDS = {
    scaffold:   ["scaffold"],
    welding:    ["welding", "weld"],
    electrical: ["electrical", "electric"],
    rigging:    ["rigging", "signalling", "signaling"],
  };

  // Normalize spaces/non-breaking spaces/weird unicode whitespace
  const normalizeTitle = (title) => {
    if (!title) return "";
    return title.toLowerCase().replace(/[\s\xa0\u200b]+/g, " ").trim();
  };

  // Get the distinctive subject from the local course title
  const getSubjectKeywords = (courseTitle) => {
    const t = courseTitle.toLowerCase();
    for (const [, words] of Object.entries(SUBJECT_KEYWORDS)) {
      if (words.some((w) => t.includes(w))) return words;
    }
    // Fallback: words > 5 chars, skip generic ones
    const SKIP = new Set(["certificate","competency","course","level","training"]);
    return courseTitle.toLowerCase().split(/\s+/).filter(w => w.length > 5 && !SKIP.has(w));
  };

  const matchesCourse = (schedTitle) => {
    if (!schedTitle) return false;
    const s = normalizeTitle(schedTitle);
    const keywords = getSubjectKeywords(course.title);
    return keywords.some((kw) => s.includes(kw));
  };

  // Use the dedicated course_level field ("Level 1" / "Level 2")
  // Fall back to checking the title string if course_level is absent
  const matchesLevel = (schedule) => {
    const lvlField = (schedule.course_level || "").toLowerCase().trim();
    const titleStr = normalizeTitle(schedule.course_title);

    if (selectedLevel === "level1") {
      if (lvlField) return lvlField === "level 1" || lvlField === "1";
      return titleStr.includes("level 1") || titleStr.includes("level1");
    }
    if (selectedLevel === "level2") {
      if (lvlField) return lvlField === "level 2" || lvlField === "2";
      return titleStr.includes("level 2") || titleStr.includes("level2");
    }
    return true;
  };

  // ─── Fetch schedules from API ─────────────────────────────────────────────
  const fetchSchedules = (forceRefresh = false) => {
    if (!course) return;
    setLoading(true);
    setError(null);

    const url = `${API_URL}?schedules=1${forceRefresh ? "&refresh=1" : ""}&t=${Date.now()}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const all = Array.isArray(data)
          ? data
          : Array.isArray(data?.schedules)
          ? data.schedules
          : [];

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        let maxYear = currentYear;
        let maxMonth = currentMonth + 2;
        if (maxMonth > 11) {
          maxYear += 1;
          maxMonth -= 12;
        }

        // Filter: match by subject keyword in title + exact course_level field + unpublished check + date validation
        const filtered = all.filter((s) => {
          if (s.registration_status === 'Unpublished Registration') return false;
          
          const titleMatch = matchesCourse(s.course_title);
          const levelMatch = matchesLevel(s);

          let dateMatch = true;
          if (s.start_date) {
            const startDate = new Date(s.start_date);
            const startYear = startDate.getFullYear();
            const startMonth = startDate.getMonth();

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Exclude past dates (older than today)
            if (startDate < today) {
              dateMatch = false;
            }
            // Exclude more than 2 months in the future
            else if (startYear > maxYear || (startYear === maxYear && startMonth > maxMonth)) {
              dateMatch = false;
            }
          }

          return titleMatch && levelMatch && dateMatch;
        });

        // Group by intake_no (or fallback if missing)
        const groups = {};
        filtered.forEach((s) => {
          const key = s.intake_no || `no-intake-${s.schedule_id || Math.random()}`;
          if (!groups[key]) {
            groups[key] = [];
          }
          groups[key].push(s);
        });

        // Map groups to aggregated intakes
        const aggregated = Object.values(groups).map((group) => {
          // Sort group by start_date so we have chronologically ordered sessions
          group.sort((a, b) => {
            const dA = a.start_date ? new Date(a.start_date) : new Date(0);
            const dB = b.start_date ? new Date(b.start_date) : new Date(0);
            return dA - dB;
          });

          const first = group[0];
          const last = group[group.length - 1];

          // Find the training session (am_mode/pm_mode !== 'Assessment')
          const trainingSession = group.find(s => 
            (s.am_mode && !s.am_mode.toLowerCase().includes('assessment')) ||
            (s.pm_mode && !s.pm_mode.toLowerCase().includes('assessment'))
          ) || first;

          // Find the assessment session
          const assessmentSession = group.find(s => 
            (s.am_mode && s.am_mode.toLowerCase().includes('assessment')) ||
            (s.pm_mode && s.pm_mode.toLowerCase().includes('assessment')) ||
            (s.examination_date)
          );

          return {
            ...trainingSession,
            start_date: first.start_date,
            end_date: last.end_date,
            examination_date: assessmentSession ? assessmentSession.start_date : (last.examination_date || last.end_date)
          };
        });

        // Sort intakes by start_date chronologically (earliest first)
        aggregated.sort((a, b) => {
          const dA = a.start_date ? new Date(a.start_date) : new Date(9999999999999);
          const dB = b.start_date ? new Date(b.start_date) : new Date(9999999999999);
          return dA - dB;
        });

        setSchedules(aggregated);
        setLoading(false);
      })
      .catch((err) => {
        console.error("EnrollPage: schedule fetch error:", err);
        setError("Could not load schedules. Please check your connection and try again.");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, selectedLevel]);

  // ─── No course context ────────────────────────────────────────────────────
  if (!course) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col pt-20">
        <Navbar />
        <div className="flex-grow flex flex-col items-center justify-center p-8">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white p-10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.04)] max-w-md w-full text-center border border-slate-100"
          >
            <div className="w-20 h-20 bg-emerald-50 text-[#0b9949] rounded-full flex items-center justify-center mx-auto mb-6 text-3xl shadow-inner border border-emerald-100">
              📭
            </div>
            <h2 className="text-2xl font-black text-slate-800 mt-4 tracking-tight">
              No Course Selected
            </h2>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              Please select a course and schedule level from our official catalog
              to view active batches.
            </p>
            <button
              onClick={() => navigate("/courses")}
              className="mt-8 w-full bg-gradient-to-r from-[#0b9949] to-[#087c3a] text-white py-3 px-6 rounded-xl font-bold hover:shadow-[0_10px_20px_rgba(11,153,73,0.2)] transition-all active:scale-[0.98] cursor-pointer border-none flex items-center justify-center gap-2 group text-sm"
            >
              <span>Browse Catalog</span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Schedule Card ────────────────────────────────────────────────────────
  const ScheduleCard = ({ schedule, index }) => {
    const maxTrainee = parseInt(schedule.max_trainee || 10);
    const currentTrainee = parseInt(schedule.current_trainee || 0);
    const remaining = maxTrainee - currentTrainee;
    const isFull = currentTrainee >= maxTrainee;

    const baseCost = parseFloat(
      schedule.course_cost || price || (selectedLevel === "level1" ? "300" : "900")
    );
    const inclGst = (baseCost * 1.09).toFixed(2);

    const handleRegisterClick = () => {
      if (isFull) return;
      navigate("/register", {
        state: {
          title: course.title,
          selectedLevel,
          price: baseCost,
          duration:
            duration ||
            `${schedule.total_hours || (selectedLevel === "level1" ? 7 : 21)} Hours`,
          schedule,
        },
      });
    };

    // Occupancy gauge
    const filledPercent = Math.min(100, Math.round((currentTrainee / maxTrainee) * 100));
    const radius = 28;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (filledPercent / 100) * circumference;

    let progressColor = "stroke-[#0b9949]";
    if (isFull) progressColor = "stroke-red-500";
    else if (remaining <= 3) progressColor = "stroke-amber-500 animate-pulse";
    else if (filledPercent > 70) progressColor = "stroke-blue-500";

    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 15, delay: index * 0.06 }}
        className="relative group bg-white border border-slate-100 hover:border-emerald-500/20 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.015)] hover:shadow-[0_20px_40px_rgba(11,153,73,0.06)] hover:-translate-y-1.5 transition-all duration-500 ease-out overflow-hidden flex flex-col lg:flex-row items-stretch text-left"
      >
        {/* Hover gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-emerald-50/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

        {/* Left – Details */}
        <div className="p-6 md:p-8 flex-grow flex flex-col justify-between z-10">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="bg-emerald-50 border border-emerald-100/50 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                {schedule.course_code || "ITE-COC"}
              </span>
              {schedule.intake_no && (
                <span className="bg-slate-50 border border-slate-200/60 text-slate-600 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                  Intake: {schedule.intake_no}
                </span>
              )}
              <span className="bg-blue-50/50 border border-blue-100/30 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
                <FaGlobe className="text-[10px] text-blue-400" />
                {schedule.course_language || "English"}
              </span>
              <span className="bg-cyan-50/50 border border-cyan-100/30 text-cyan-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
                <FaHourglassHalf className="text-[10px] text-cyan-400" />
                {schedule.total_hours || (selectedLevel === "level1" ? 7 : 21)} Hrs
              </span>
            </div>

            <h4 className="text-slate-800 font-extrabold text-lg md:text-xl tracking-tight leading-snug group-hover:text-[#0b9949] transition-colors duration-300 mb-5 uppercase">
              {schedule.course_title}
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Training Dates */}
              <div className="bg-slate-50/40 hover:bg-slate-50 border border-slate-100/80 rounded-xl p-3.5 flex items-start gap-3 transition-colors duration-300">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600 mt-0.5 shadow-sm">
                  <FaCalendarAlt className="text-xs" />
                </div>
                <div>
                  <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                    TRAINING DATES
                  </span>
                  <span className="font-bold text-slate-800 text-xs leading-tight">
                    {formatDateString(schedule.start_date)}
                    {schedule.end_date &&
                      schedule.end_date !== schedule.start_date &&
                      ` – ${formatDateString(schedule.end_date)}`}
                  </span>
                </div>
              </div>

              {/* Timings */}
              <div className="bg-slate-50/40 hover:bg-slate-50 border border-slate-100/80 rounded-xl p-3.5 flex items-start gap-3 transition-colors duration-300">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600 mt-0.5 shadow-sm">
                  <FaClock className="text-xs" />
                </div>
                <div>
                  <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                    TIMINGS
                  </span>
                  <span className="font-bold text-slate-800 text-xs leading-tight">
                    {formatTime(schedule)}
                  </span>
                </div>
              </div>

              {/* Assessment Date */}
              <div className="bg-slate-50/40 hover:bg-slate-50 border border-slate-100/80 rounded-xl p-3.5 flex items-start gap-3 transition-colors duration-300">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600 mt-0.5 shadow-sm">
                  <FaClipboardCheck className="text-xs" />
                </div>
                <div>
                  <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                    ASSESSMENT DATE
                  </span>
                  <span className="font-bold text-slate-800 text-xs leading-tight">
                    {formatDateString(
                      schedule.examination_date ||
                        schedule.end_date ||
                        schedule.start_date
                    )}
                  </span>
                </div>
              </div>

              {/* Venue */}
              <div className="bg-slate-50/40 hover:bg-slate-50 border border-slate-100/80 rounded-xl p-3.5 flex items-start gap-3 transition-colors duration-300">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600 mt-0.5 shadow-sm">
                  <FaMapMarkerAlt className="text-xs" />
                </div>
                <div>
                  <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">
                    TRAINING VENUE
                  </span>
                  <span
                    className="font-bold text-slate-800 text-xs leading-tight truncate block max-w-[200px]"
                    title={schedule.location_name}
                  >
                    {schedule.location_name || "GreenMark Training Center"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Middle – Occupancy Gauge */}
        <div className="w-full lg:w-[220px] bg-slate-50/40 lg:border-l lg:border-r border-y lg:border-y-0 border-slate-100 p-6 flex flex-col items-center justify-center text-center gap-3.5 z-10">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="40" cy="40" r={radius} className="stroke-slate-200" strokeWidth="5" fill="transparent" />
              <circle
                cx="40" cy="40" r={radius}
                className={`${progressColor} transition-all duration-1000 ease-out`}
                strokeWidth="5" fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-lg font-black text-slate-800 leading-none">{currentTrainee}</span>
              <div className="h-[1px] w-6 bg-slate-300 my-1" />
              <span className="text-[10px] font-bold text-slate-400 leading-none">{maxTrainee}</span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[9px] font-extrabold text-slate-400 tracking-widest uppercase">CLASS OCCUPANCY</span>
            {isFull ? (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[10px] font-black bg-red-50 border border-red-100 text-red-600">
                <FaLock className="text-[9px]" /> FULLY BOOKED
              </span>
            ) : remaining <= 3 ? (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[10px] font-black bg-amber-50 border border-amber-200 text-amber-700 animate-pulse">
                <FaExclamationTriangle className="text-[9px]" /> ONLY {remaining} LEFT
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[10px] font-black bg-emerald-50 border border-emerald-100 text-emerald-700">
                🟢 {remaining} VACANCIES
              </span>
            )}
          </div>
        </div>

        {/* Right – Price + CTA */}
        <div className="w-full lg:w-[240px] p-6 md:p-8 flex flex-col justify-center items-center lg:items-end text-center lg:text-right gap-5 z-10">
          <div className="flex flex-col items-center lg:items-end">
            <span className="text-[9px] font-extrabold text-slate-400 tracking-widest uppercase mb-1.5">
              COURSE INVESTMENT
            </span>
            <div className="text-3xl font-black text-[#0b9949] tracking-tight leading-none">
              ${inclGst}
            </div>
            <div className="text-[9px] font-bold text-slate-400 tracking-widest uppercase mt-1.5">
              INCL. 9% GST
            </div>
            <div className="text-[11px] font-medium text-slate-400 mt-1">
              (${baseCost.toFixed(2)} excl. GST)
            </div>
          </div>

          {isFull ? (
            <button
              disabled
              className="w-full py-4 px-6 bg-slate-200 text-slate-400 font-extrabold rounded-xl shadow-none cursor-not-allowed uppercase text-xs tracking-wider flex items-center justify-center gap-2 border border-slate-200"
            >
              <FaLock className="text-xs" /> Class Closed
            </button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRegisterClick}
              className="w-full relative overflow-hidden bg-gradient-to-r from-[#0b9949] to-[#087c3a] text-white font-extrabold text-xs uppercase tracking-wider py-4 px-6 rounded-xl shadow-[0_6px_20px_rgba(11,153,73,0.18)] hover:shadow-[0_12px_25px_rgba(11,153,73,0.28)] transition-all flex items-center justify-center gap-2 group cursor-pointer border-none"
            >
              <span>Enroll Now</span>
              <FaArrowRight className="text-white text-xs group-hover:translate-x-1.5 transition-transform duration-300" />
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  // ─── Loading skeleton ─────────────────────────────────────────────────────
  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-100 p-8 flex flex-col lg:flex-row gap-6 animate-pulse">
          <div className="flex-grow space-y-3">
            <div className="flex gap-2">
              <div className="h-6 w-24 bg-slate-100 rounded-full" />
              <div className="h-6 w-32 bg-slate-100 rounded-full" />
              <div className="h-6 w-20 bg-slate-100 rounded-full" />
            </div>
            <div className="h-5 w-3/4 bg-slate-100 rounded-lg" />
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[1,2,3,4].map(j => (
                <div key={j} className="h-16 bg-slate-50 rounded-xl border border-slate-100" />
              ))}
            </div>
          </div>
          <div className="w-full lg:w-[220px] flex flex-col items-center justify-center gap-3">
            <div className="w-20 h-20 rounded-full bg-slate-100" />
            <div className="h-6 w-28 bg-slate-100 rounded-full" />
          </div>
          <div className="w-full lg:w-[240px] flex flex-col items-center gap-4">
            <div className="h-10 w-32 bg-slate-100 rounded-lg" />
            <div className="h-12 w-full bg-slate-100 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="bg-slate-50 text-slate-900 font-sans min-h-screen flex flex-col pt-20">
      <Navbar />

      {/* Banner */}
      <div
        className="relative h-[280px] bg-fixed bg-center bg-cover overflow-hidden"
        style={{ backgroundImage: "url('/mission.avif')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/85" />
        <div className="absolute -top-12 -left-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-12 right-12 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 max-w-4xl mx-auto z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4"
          >
            <button
              onClick={() => navigate(-1)}
              className="bg-white/10 hover:bg-white/20 text-white px-3.5 py-1.5 rounded-full transition-all text-xs font-bold flex items-center gap-1.5 backdrop-blur-md border border-white/15 hover:scale-105 active:scale-95 shadow-sm cursor-pointer"
            >
              <FaChevronLeft className="text-[10px]" /> Back to Details
            </button>
            <span className="bg-gradient-to-r from-[#0b9949] to-[#087c3a] text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-md border border-emerald-400/20">
              {selectedLevel === "level1" ? "Level 1 Intake" : "Level 2 Intake"}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 100 }}
            className="text-2xl sm:text-3xl md:text-4xl font-black mb-3 uppercase tracking-tight leading-tight text-white drop-shadow-md"
          >
            {course.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xs md:text-sm text-emerald-100 max-w-2xl font-medium tracking-wide leading-relaxed bg-emerald-950/20 backdrop-blur-[2px] py-1.5 px-5 rounded-full border border-emerald-500/10"
          >
            Please choose from the verified intake batches below to proceed with your enrollment.
          </motion.p>
        </div>
      </div>

      {/* Schedules Section */}
      <div className="max-w-5xl mx-auto px-4 py-12 w-full flex-grow">
        {/* Header row */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Available Intakes
              {!loading && !error && (
                <span className="text-[#0b9949] text-sm font-black bg-emerald-50 border border-emerald-100 px-3 py-0.5 rounded-full">
                  {schedules.length}
                </span>
              )}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Live schedules fetched directly from our training management system.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Refresh button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95, rotate: 180 }}
              onClick={() => fetchSchedules(true)}
              disabled={loading}
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all shadow-sm cursor-pointer disabled:opacity-50"
            >
              <FaSync className={`text-[10px] ${loading ? "animate-spin" : ""}`} />
              Refresh
            </motion.button>

            {/* Duration / Language info */}
            {!loading && schedules.length > 0 && (
              <div className="flex items-center gap-4 bg-white px-4 py-3 rounded-xl border border-slate-100 shadow-sm self-start sm:self-auto text-xs text-slate-600 font-bold">
                <div>Duration: <b className="text-slate-800">{duration || `${schedules[0]?.total_hours || (selectedLevel === "level1" ? 7 : 21)} Hours`}</b></div>
                <div className="h-4 w-[1px] bg-slate-200" />
                <div>Language: <b className="text-slate-800">{schedules[0]?.course_language || "English"}</b></div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <LoadingSkeleton />
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white border border-red-100 rounded-3xl p-16 text-center shadow-sm max-w-lg mx-auto"
            >
              <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl border border-red-100">
                ⚠️
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 tracking-tight">
                Failed to Load Schedules
              </h3>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{error}</p>
              <button
                onClick={() => fetchSchedules(true)}
                className="mt-8 bg-gradient-to-r from-[#0b9949] to-[#087c3a] text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all text-sm flex items-center gap-2 mx-auto cursor-pointer border-none"
              >
                <FaSync /> Try Again
              </button>
            </motion.div>
          ) : schedules.length > 0 ? (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {schedules.map((sched, index) => (
                <ScheduleCard
                  key={sched.schedule_id || sched.id || index}
                  schedule={sched}
                  index={index}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white border border-slate-100 rounded-3xl p-16 text-center shadow-[0_20px_50px_rgba(0,0,0,0.03)] max-w-lg mx-auto"
            >
              <div className="w-20 h-20 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl shadow-inner border border-slate-100">
                📭
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mt-4 tracking-tight">
                No Batches Currently Scheduled
              </h3>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                There are no active public classes scheduled for{" "}
                <b>{course.title}</b> ({selectedLevel === "level1" ? "Level 1" : "Level 2"}) at this time. Please check back soon or contact us directly.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="mt-8 border border-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold hover:bg-slate-50 hover:text-slate-800 transition-all active:scale-[0.98] cursor-pointer shadow-sm text-xs tracking-wider uppercase"
              >
                Go Back
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Footer />
    </div>
  );
}
